import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, User, ChevronRight, ArrowRight, TrendingUp } from 'lucide-react';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { DynamicSeoHead } from '../components/DynamicSeoHead';
import { supabase } from '../lib/supabase';

const PILLAR_LINKS = [
  { label: 'Gestion de trésorerie', href: '/gestion-tresorerie' },
  { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
  { label: 'Prévision de trésorerie', href: '/prevision-tresorerie' },
  { label: 'Logiciel trésorerie', href: '/logiciel-tresorerie' },
  { label: 'Logiciel trésorerie association', href: '/logiciel-tresorerie-association' },
  { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
];

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  published_at: string;
  featured_image_url?: string;
  featured_image_alt?: string;
  author_name: string;
  categories: Array<{ id: string; name: string; slug: string }>;
  tags: Array<{ id: string; name: string; slug: string }>;
  meta_title: string | null;
  meta_description: string | null;
  canonical_url: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image_url?: string;
}

interface RelatedPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featured_image_url?: string;
}

interface TocItem {
  id: string;
  text: string;
  level: number;
}

export function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);
  const [toc, setToc] = useState<TocItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchPost();
    }
  }, [slug]);

  async function fetchPost() {
    setLoading(true);
    setNotFound(false);

    const { data: postData, error } = await supabase
      .from('blog_posts')
      .select(`
        id,
        title,
        slug,
        content,
        excerpt,
        published_at,
        featured_image_id,
        og_image_id,
        author_id,
        meta_title,
        meta_description,
        canonical_url,
        og_title,
        og_description,
        user_profiles!blog_posts_author_id_fkey (
          first_name,
          last_name
        )
      `)
      .eq('slug', slug)
      .eq('status', 'published')
      .lte('published_at', new Date().toISOString())
      .maybeSingle();

    if (error || !postData) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let featuredImageUrl = '';
    let featuredImageAlt = '';
    let ogImageUrl = '';

    if (postData.featured_image_id) {
      const { data: media } = await supabase
        .from('media')
        .select('url, alt')
        .eq('id', postData.featured_image_id)
        .maybeSingle();

      if (media) {
        featuredImageUrl = media.url;
        featuredImageAlt = media.alt || '';
      }
    }

    if (postData.og_image_id) {
      const { data: ogMedia } = await supabase
        .from('media')
        .select('url')
        .eq('id', postData.og_image_id)
        .maybeSingle();

      if (ogMedia) {
        ogImageUrl = ogMedia.url;
      }
    }

    const { data: postCategories } = await supabase
      .from('blog_post_categories')
      .select('category_id, blog_categories (id, name, slug)')
      .eq('post_id', postData.id);

    const categories = postCategories?.map((pc: any) => ({
      id: pc.blog_categories.id,
      name: pc.blog_categories.name,
      slug: pc.blog_categories.slug
    })) || [];

    const { data: postTags } = await supabase
      .from('blog_post_tags')
      .select('tag_id, blog_tags (id, name, slug)')
      .eq('post_id', postData.id);

    const tags = postTags?.map((pt: any) => ({
      id: pt.blog_tags.id,
      name: pt.blog_tags.name,
      slug: pt.blog_tags.slug
    })) || [];

    const fullPost: BlogPost = {
      id: postData.id,
      title: postData.title,
      slug: postData.slug,
      content: postData.content,
      excerpt: postData.excerpt,
      published_at: postData.published_at,
      featured_image_url: featuredImageUrl,
      featured_image_alt: featuredImageAlt,
      og_image_url: ogImageUrl || featuredImageUrl,
      author_name: postData.user_profiles
        ? `${postData.user_profiles.first_name || ''} ${postData.user_profiles.last_name || ''}`.trim()
        : 'Trezoone',
      categories,
      tags,
      meta_title: postData.meta_title,
      meta_description: postData.meta_description,
      canonical_url: postData.canonical_url,
      og_title: postData.og_title,
      og_description: postData.og_description
    };

    setPost(fullPost);
    generateToc(postData.content);
    fetchRelatedPosts(postData.id, categories.map(c => c.id));
    setLoading(false);
  }

  function generateToc(content: string) {
    const headings: TocItem[] = [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    const headingElements = tempDiv.querySelectorAll('h2, h3');
    headingElements.forEach((heading, index) => {
      const id = `heading-${index}`;
      const text = heading.textContent || '';
      const level = parseInt(heading.tagName.substring(1));

      headings.push({ id, text, level });
    });

    setToc(headings);
  }

  async function fetchRelatedPosts(currentPostId: string, categoryIds: string[]) {
    if (categoryIds.length === 0) return;

    const { data: relatedPostIds } = await supabase
      .from('blog_post_categories')
      .select('post_id')
      .in('category_id', categoryIds)
      .neq('post_id', currentPostId)
      .limit(3);

    if (!relatedPostIds || relatedPostIds.length === 0) return;

    const uniquePostIds = [...new Set(relatedPostIds.map(rp => rp.post_id))].slice(0, 3);

    const { data: posts } = await supabase
      .from('blog_posts')
      .select('id, title, slug, excerpt, featured_image_id')
      .in('id', uniquePostIds)
      .eq('status', 'published')
      .lte('published_at', new Date().toISOString());

    if (posts) {
      const postsWithImages = await Promise.all(
        posts.map(async (p) => {
          let featuredImageUrl = '';

          if (p.featured_image_id) {
            const { data: media } = await supabase
              .from('media')
              .select('url')
              .eq('id', p.featured_image_id)
              .maybeSingle();

            if (media) {
              featuredImageUrl = media.url;
            }
          }

          return {
            id: p.id,
            title: p.title,
            slug: p.slug,
            excerpt: p.excerpt,
            featured_image_url: featuredImageUrl
          };
        })
      );

      setRelatedPosts(postsWithImages);
    }
  }

  function processContent(content: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    const headingElements = tempDiv.querySelectorAll('h2, h3');
    headingElements.forEach((heading, index) => {
      heading.id = `heading-${index}`;
    });

    return tempDiv.innerHTML;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MarketingHeader
          onNavigate={(path) => navigate(path)}
          onSignIn={() => navigate('/login')}
          onSignUp={() => navigate('/login')}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article introuvable</h1>
          <p className="text-gray-600 mb-8">Cet article n'existe pas ou n'est plus disponible.</p>
          <button
            onClick={() => navigate('/blog')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Retour au blog
          </button>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "datePublished": post.published_at,
    "dateModified": post.published_at,
    "author": {
      "@type": "Person",
      "name": post.author_name
    },
    "image": post.featured_image_url || "https://trezoone.fr/logo_trezoone.png",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://trezoone.fr/blog/${post.slug}`
    },
    "description": post.excerpt
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://trezoone.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://trezoone.fr/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://trezoone.fr/blog/${post.slug}`
      }
    ]
  };

  return (
    <>
      <DynamicSeoHead
        slug={`/blog/${post.slug}`}
        fallback={{
          title: post.meta_title || `${post.title} – Blog Trezoone`,
          description: post.meta_description || post.excerpt,
          keywords: post.tags.map(t => t.name).join(', '),
          canonical: post.canonical_url || undefined,
          ogTitle: post.og_title || undefined,
          ogDescription: post.og_description || undefined,
          ogImage: post.og_image_url || undefined,
          jsonLd: [articleJsonLd, breadcrumbJsonLd]
        }}
      />

      <div className="min-h-screen bg-gray-50">
        <MarketingHeader
          onNavigate={(path) => navigate(path)}
          onSignIn={() => navigate('/login')}
          onSignUp={() => navigate('/login')}
        />

        <div className="bg-blue-600 py-4 px-4">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-white">
              <TrendingUp size={18} />
              <span className="font-semibold text-sm">Anticipez vos tensions de trésorerie avant qu'elles arrivent</span>
            </div>
            <div className="flex gap-3">
              <Link
                to="/beta-founders"
                className="inline-flex items-center gap-1.5 bg-white text-blue-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-50 transition-colors"
              >
                Tester Trezoone <ArrowRight size={14} />
              </Link>
              <Link
                to="/simulateur-tresorerie-association"
                className="inline-flex items-center gap-1.5 border border-blue-400 text-white px-4 py-2 rounded-lg text-sm hover:border-white transition-colors"
              >
                Simuler ma trésorerie
              </Link>
            </div>
          </div>
        </div>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <nav className="flex items-center gap-2 text-sm text-gray-600 mb-8">
            <button onClick={() => navigate('/')} className="hover:text-blue-600">
              Accueil
            </button>
            <ChevronRight className="w-4 h-4" />
            <button onClick={() => navigate('/blog')} className="hover:text-blue-600">
              Blog
            </button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium truncate">{post.title}</span>
          </nav>

          <div className="grid lg:grid-cols-4 gap-12">
            <article className="lg:col-span-3">
              {post.featured_image_url && (
                <img
                  src={post.featured_image_url}
                  alt={post.featured_image_alt}
                  className="w-full h-96 object-cover rounded-xl mb-8"
                  loading="eager"
                />
              )}

              <div className="flex items-center gap-3 mb-6">
                {post.categories.map((cat) => (
                  <span
                    key={cat.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    {cat.name}
                  </span>
                ))}
              </div>

              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {post.title}
              </h1>

              <div className="flex items-center gap-6 text-gray-600 mb-8 pb-8 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span>{post.author_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>
                    {new Date(post.published_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>

              <div
                className="blog-content max-w-none mb-12"
                dangerouslySetInnerHTML={{ __html: processContent(post.content) }}
              />

              {post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-12">
                  {post.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                    >
                      #{tag.name}
                    </span>
                  ))}
                </div>
              )}

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 rounded-xl text-white">
                <p className="text-xs font-semibold text-blue-400 uppercase tracking-wide mb-2">Trezoone</p>
                <h3 className="text-2xl font-bold mb-3">
                  Si rien ne rentre pendant 2 mois, combien de temps vous tenez ?
                </h3>
                <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                  Trezoone transforme vos données bancaires en plan de trésorerie sur 12 mois. Voyez vos tensions à venir — avant qu'elles arrivent. Essai gratuit 14 jours, sans carte bancaire.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/beta-founders"
                    className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 font-bold transition-colors"
                  >
                    Tester Trezoone gratuitement <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/simulateur-tresorerie-association"
                    className="inline-flex items-center justify-center gap-2 border border-gray-600 text-gray-300 px-6 py-3 rounded-xl hover:border-gray-400 hover:text-white transition-colors"
                  >
                    Simuler ma trésorerie
                  </Link>
                </div>
              </div>

              <div className="mt-8 p-5 bg-gray-50 rounded-xl border border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Guides de trésorerie</h4>
                <div className="flex flex-wrap gap-2">
                  {PILLAR_LINKS.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="flex items-center gap-1 text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-300 hover:text-blue-600 transition-all bg-white"
                    >
                      {link.label} <ChevronRight size={12} />
                    </Link>
                  ))}
                </div>
              </div>
            </article>

            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {toc.length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Table des matières</h3>
                    <nav className="space-y-2">
                      {toc.map((item) => (
                        <a
                          key={item.id}
                          href={`#${item.id}`}
                          className={`block text-sm hover:text-blue-600 transition-colors ${
                            item.level === 3 ? 'pl-4 text-gray-600' : 'text-gray-700 font-medium'
                          }`}
                        >
                          {item.text}
                        </a>
                      ))}
                    </nav>
                  </div>
                )}

                <div className="bg-blue-600 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp size={18} />
                    <span className="font-bold text-sm">Trezoone</span>
                  </div>
                  <p className="text-sm text-blue-100 mb-4 leading-relaxed">
                    Anticipez vos tensions de trésorerie sur 12 mois. Essai gratuit 14 jours.
                  </p>
                  <Link
                    to="/beta-founders"
                    className="block text-center bg-white text-blue-700 px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-50 transition-colors mb-2"
                  >
                    Tester gratuitement
                  </Link>
                  <Link
                    to="/simulateur-tresorerie-association"
                    className="block text-center border border-blue-400 text-white px-4 py-2.5 rounded-xl text-sm hover:border-white transition-colors"
                  >
                    Simuler ma trésorerie
                  </Link>
                  <p className="text-xs text-blue-200 mt-3 text-center">Sans carte · Sans engagement</p>
                </div>
              </div>
            </aside>
          </div>

          {relatedPosts.length > 0 && (
            <section className="mt-16 pt-16 border-t border-gray-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Articles similaires</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <article
                    key={relatedPost.id}
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                  >
                    {relatedPost.featured_image_url && (
                      <img
                        src={relatedPost.featured_image_url}
                        alt={relatedPost.title}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                      />
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-blue-600 transition-colors">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-600 line-clamp-3">{relatedPost.excerpt}</p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </main>

        <MarketingFooter />
      </div>
    </>
  );
}
